<template>
  <div class="date-picker-root" :style="getRootStyle">
    <div class="form-label label-text" v-if="label">
      {{ $t(label) }}
      <span v-if="is_required" class="req-sign">*</span>
    </div>
    <validation-provider :name="name" :rules="rules" v-slot="{ errors }" :id="id">
      <input type="text" v-model="inputfield" class="d-none" />
      <div class="d-flex align-items-center flex-wrap">
        <div style="margin-right: 0.5rem">
          <div class="select disabled" :style="cus_style" v-if="disabled || readOnly">
            {{ selected_day?.text || selected_day || $t('day') }}
          </div>
          <b-form-select
            v-else
            :class="[
              'select',
              {
                'invalid-select':
                  (is_invalid || (errors && errors.length)) && selected_day === null ? true : false,
              },
            ]"
            :label="label"
            :disabled="disabled ? true : false"
            :placeholder="placeholder"
            v-model="selected_day"
            :style="cus_style"
            :readonly="readOnly"
            :ref="cus_ref"
            :options="day_options"
            id="day"
          />
        </div>
        <div style="margin-right: 0.5rem">
          <div class="select disabled" :style="cus_style" v-if="disabled || readOnly">
            {{
              selected_month?.text
                ? selected_month.text
                : selected_month !== null && selected_month !== undefined
                ? selected_month + 1
                : $t('month')
            }}
          </div>
          <b-form-select
            v-else
            :class="[
              'select',
              {
                'invalid-select':
                  (is_invalid || (errors && errors.length)) && selected_month === null
                    ? true
                    : false,
              },
            ]"
            :label="label"
            :disabled="disabled ? true : false"
            :placeholder="placeholder"
            v-model="selected_month"
            :style="cus_style"
            :readonly="readOnly"
            :ref="cus_ref"
            :options="month_options"
            id="month"
          />
        </div>
        <div style="margin-right: 0.5rem">
          <div class="select disabled" :style="cus_style" v-if="disabled || readOnly">
            {{ selected_year?.text || selected_year || $t('year') }}
          </div>
          <b-form-select
            v-else
            :class="[
              'select',
              {
                'invalid-select':
                  (is_invalid || (errors && errors.length)) && selected_year === null
                    ? true
                    : false,
              },
            ]"
            :label="label"
            :disabled="disabled ? true : false"
            :placeholder="placeholder"
            v-model="selected_year"
            :style="cus_style"
            :readonly="readOnly"
            :ref="cus_ref"
            :options="year_options"
            id="year"
          />
        </div>
      </div>
      <div v-if="helper_text" class="helper-text" :style="helper_text_style">
        {{ $t(helper_text) }}
      </div>
      <div class="error-text" :style="helper_text_style" v-if="errors && errors.length">
        {{ errors[0] }}
      </div>
      <slot name="attachment"></slot>
    </validation-provider>
  </div>
</template>

<script>
const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export default {
  emits: ['change-value', 'change'],
  props: [
    'label',
    'placeholder',
    'is_invalid',
    'helper_text',
    'helper_text_style',
    'field',
    'value',
    'cus_style',
    'remove_default_margin',
    'readOnly',
    'disabled',
    'cus_ref',
    'is_required',
    'options',
    'rules',
    'name',
    'id',
    'not_future_date',
    'up_to_future_years',
  ],
  data() {
    return {
      day_options: [],
      month_options: [],
      year_options: [],
      selected_day: this.value ? new Date(this.value).getDate() : null,
      selected_month: this.value ? new Date(this.value).getMonth() : null,
      selected_year: this.value ? new Date(this.value).getFullYear() : null,
      inputfield: this.value,
    }
  },
  computed: {
    getRootStyle() {
      return `margin-bottom: ${this.remove_default_margin ? '0px' : '20px'};position:relative;`
    },
  },
  methods: {
    setVal() {
      if (this.selected_day !== null && this.selected_month !== null && this.selected_year !== null)
        this.inputfield = new Date(this.selected_year, this.selected_month, this.selected_day)
      else this.inputfield = null
    },
    setDaysOptions() {
      const days = new Date(
        this.selected_year || new Date().getFullYear(),
        this.selected_month + 1 || 0,
        0,
        12
      ).getDate()

      let arr = [{ value: null, text: this.$t('day') }]
      for (let i = 1; i <= days; i++) {
        arr.push({ value: i, text: i })
      }
      this.day_options = arr

      if (this.selected_day && this.selected_day > days) this.selected_day = null
    },
    setMonthsOptions() {
      let arr = [{ value: null, text: this.$t('month') }]
      for (let i = 0; i < monthNames.length; i++) {
        arr.push({ value: i, text: this.$t(monthNames[i]) })
      }
      this.month_options = arr
    },
    setYearsOptions() {
      let yr = 2022,
        arr = [{ value: null, text: this.$t('year') }]

      if (this.not_future_date) {
        for (let i = 0; i <= 70; i++) {
          arr.push({ value: yr - 70 + i, text: yr - 70 + i })
        }
      } else {
        for (let i = 0; i < 20; i++) {
          arr.push({ value: yr + i, text: yr + i })
        }
      }

      if (this.up_to_future_years) {
        const cur_yr = new Date().getFullYear()
        for (let i = yr; i <= cur_yr; i++) {
          if (!arr?.find((each) => each.value === i)) arr.push({ value: i, text: i })
        }
        for (let i = 1; i <= this.up_to_future_years; i++) {
          if (!arr?.find((each) => each.value === cur_yr + i))
            arr.push({ value: cur_yr + i, text: cur_yr + i })
        }
      }

      this.year_options = arr
    },
  },
  watch: {
    value(val) {
      if (!val && this.selected_day) this.selected_day = null
      if (
        val &&
        (this.selected_day === null ||
          (this.selected_day && this.selected_day !== new Date(val).getDate()))
      )
        this.selected_day = new Date(val).getDate()

      if (!val && this.selected_month !== null) this.selected_month = null
      if (
        val &&
        (this.selected_month === null ||
          (this.selected_month && this.selected_month !== new Date(val).getMonth()))
      )
        this.selected_month = new Date(val).getMonth()

      if (!val && this.selected_year) this.selected_year = null
      if (
        val &&
        (this.selected_year === null ||
          (this.selected_year && this.selected_year !== new Date(val).getFullYear()))
      )
        this.selected_year = new Date(val).getFullYear()

      if (!val && this.inputfield) this.inputfield = null
      if (this.inputfield === null) this.inputfield = val
    },
    inputfield() {
      if (this.inputfield == 'Thu Jan 01 1970 12:00:00 GMT+0200 (GMT+03:00)') {
        this.selected_day = null
        this.selected_month = null
        this.selected_year = null
        this.$emit('change-value', 'reset', this.field)
      } else {
        this.$emit('change-value', this.inputfield, this.field)
        this.$emit('change', this.inputfield)
      }
    },
    selected_day(val) {
      this.setVal()
    },
    selected_month(val) {
      this.setVal()
      if (val !== null) {
        this.setDaysOptions()
      }
    },
    selected_year(val) {
      this.setVal()
      if (val !== null) {
        this.setDaysOptions()
      }
    },
  },
  mounted() {
    this.setDaysOptions()
    this.setMonthsOptions()
    this.setYearsOptions()
  },
}
</script>

<style lang="scss" scoped>
.label-text {
  font-size: 14px;
  color: var(--color-61);
  margin-bottom: 5px;
  font-weight: 700;
}
.invalid-select {
  border-color: var(--color-47) !important;
  background-color: var(--color-01) !important;
}
.select {
  width: 100%;
  height: 40px;
  font-size: 14px;
  padding: 10px;
  background: var(--color-01);
  color: var(--color-61);
  border: 1px solid;
  border-radius: 6px;
  border-color: var(--color-64);
  box-shadow: 0px 1px 2px var(--color-37);
  font-weight: 400;

  &:focus-visible {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:hover {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:focus {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:disabled {
    background-color: var(--color-67);
  }
}
</style>
